import { Button, createStyles, FormControl, Grid, InputLabel, makeStyles, MenuItem, Select, TextField, Theme, Typography } from '@material-ui/core';
import * as React from 'react';
import { useState } from 'react';
import { BulkResponseDialog } from '../common/BulkResponseDialog';
import Icon, { SupportedIcons } from '../common/Icon';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { AuthContext } from '../_auth/AuthContext';
import { DiscountMemberships } from '../_service/bulk';

import { BulkDiscountRequest, BulkUtilResponse } from '../_types/bulk';
import { ISearchResult } from '../_types/search';
import { ServicePlan } from '../_types/serviceplan';
import { ILocationSummary } from '../_types/user';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';
import { BulkListInput, BulkListValue, BulkListValueType } from '../common/BulkListInput';
import { buildBulkMemberSelection, BulkMemberSelectionMode } from '../_util/bulkMemberSelection';

interface Props {
  plans: ServicePlan[]
}

interface ListModeConfig {
  acceptedHeaders: string[];
  label: string;
  valueType: BulkListValueType;
}

const listModeConfigs: Record<Exclude<BulkMemberSelectionMode, 'location'>, ListModeConfig> = {
  membership_ids: {
    acceptedHeaders: ['membership_id', 'membership_ids', 'membership id', 'membership ids'],
    label: 'Membership IDs',
    valueType: 'number',
  },
  user_ids: {
    acceptedHeaders: ['user_id', 'user_ids', 'user id', 'user ids'],
    label: 'User IDs',
    valueType: 'number',
  },
  user_emails: {
    acceptedHeaders: ['email', 'emails', 'user_email', 'user_emails', 'user email', 'user emails'],
    label: 'User emails',
    valueType: 'email',
  },
};

const useStyles = makeStyles((theme: Theme) => 
    createStyles({
        dateContainer: {
          marginTop: 10,
          marginBottom: 10,
          padding: theme.spacing(1)
        },
        inputLabel: {
          fontSize: 14
        },
        icon: {
          width: 12,
          height: 12,
          cursor: 'pointer'
        }
    })
);

const DiscountMembers: React.FC<Props> = (prop: Props) => {
    const { user } = React.useContext(AuthContext);
    const [location, setLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>();
    const [selectionMode, setSelectionMode] = useState<BulkMemberSelectionMode>('location');
    const [bulkListValues, setBulkListValues] = useState<BulkListValue[]>([]);
    const [bulkListError, setBulkListError] = useState<string | null>(null);
    const [listInputVersion, setListInputVersion] = useState<number>(0);
    const [couponId, setCouponId] = useState<string>('');
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [emailList, setEmailList] = useState<string[]>([]);
    const [ignoreList, setIgnoreList] = useState<string[]>([]);
    const [storedEmailList, setStoredEmailList] = useState<string[] | undefined>();
    const [storedIgnoreList, setStoredIgnoreList] = useState<string[] | undefined>();
  
    const classes = useStyles();

    const handleSelectionModeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      setSelectionMode(event.target.value as BulkMemberSelectionMode);
      setSelectedPlan(null);
      setBulkListValues([]);
      setBulkListError(null);
    };

    const _submit = async() => {
      const trimmedCouponId = couponId.trim();
      const requiresLocation = selectionMode !== 'membership_ids';

      if (requiresLocation && !location) return alert('Please select a location.');
      if (!trimmedCouponId) return alert('Please select a coupon id to apply');
      if (selectionMode !== 'location' && bulkListError) return alert(bulkListError);
      if (selectionMode !== 'location' && bulkListValues.length === 0) {
        return alert(`Please enter at least one ${listModeConfigs[selectionMode].label.toLowerCase()}.`);
      }

      const servicePlanId = selectedPlan && selectedPlan !== 'all plans'
        ? Number(selectedPlan)
        : undefined;
      const memberSelection = buildBulkMemberSelection({
        locationId: location?.id,
        mode: selectionMode,
        servicePlanId,
        values: bulkListValues,
      });
      const request: BulkDiscountRequest = {
        ...memberSelection,
        coupon_id: trimmedCouponId,
      };
      let response: BulkUtilResponse | null;

      setInProgress(true);
      try {
        response = await DiscountMemberships(request);
      } catch(error) {
        setInProgress(false);
        return alert('Something went wrong while connecting to the API. Please try again.');
      }

      if (response === null) {
        setInProgress(false);
        return alert('Something went wrong while connecting to the API. Check that the coupon id exists in Stripe and try again.');
      }

      if (response !== null) {
        setInProgress(false);
        const hasSuccessfulDiscounts = Boolean(response.emails && response.emails.length > 0);

        if (hasSuccessfulDiscounts) {
          setSelectedPlan(null);

          if (selectionMode !== 'location') {
            setBulkListValues([]);
            setBulkListError(null);
            setListInputVersion(version => version + 1);
          }
        }

        if (response.emails && response.emails.length > 0) {
          setEmailList(response.emails);
          setIsOpen(true);
        }
        
        if (response.ignoreList && response.ignoreList.length > 0) {
          setIgnoreList(response.ignoreList);
          setIsOpen(true);
        }
  
        if (response.emails && response.emails.length === 0
          && response.ignoreList && response.ignoreList.length === 0) {
            return alert('No memberships were discounted. Make sure you selected the correct members, location, and/or plan.');
          }
      }
    }

    return (
      <>
        <Grid item>
          <h3>Apply Discount</h3>
          <p>Choose which members should receive the discount, then enter the coupon ID you wish to apply.</p>
        </Grid>

        <Grid container className={classes.dateContainer}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="discount-selection-mode-label">Target members by</InputLabel>
            <Select
              label="Target members by"
              labelId="discount-selection-mode-label"
              onChange={handleSelectionModeChange}
              value={selectionMode}>
              <MenuItem value="location">Location and optional plan</MenuItem>
              <MenuItem value="membership_ids">Membership IDs</MenuItem>
              <MenuItem value="user_ids">User IDs</MenuItem>
              <MenuItem value="user_emails">User emails</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {selectionMode !== 'membership_ids' &&
          <PlanByLocationSelector
            onLocationChange={(newValue: ISearchResult | null) => {
                setLocation(newValue);
            }}
            onPlanChange={(event: any) => setSelectedPlan(event.target.value as string)}
            planValue={selectedPlan || ''}
            plans={prop.plans}
            planInputLabel='Plans'
          />}

        {selectionMode !== 'location' &&
          <Grid container className={classes.dateContainer}>
            <BulkListInput
              acceptedHeaders={listModeConfigs[selectionMode].acceptedHeaders}
              key={`${selectionMode}-${listInputVersion}`}
              label={listModeConfigs[selectionMode].label}
              onChange={(values, error) => {
                setBulkListValues(values);
                setBulkListError(error);
              }}
              valueType={listModeConfigs[selectionMode].valueType}
            />
            {!bulkListError && bulkListValues.length > 0 &&
              <Typography variant="caption">
                {bulkListValues.length} distinct value{bulkListValues.length === 1 ? '' : 's'} ready.
              </Typography>}
          </Grid>}
        <Grid container 
          className={classes.dateContainer}
          justify="space-evenly"
          direction="row">
            <FormControl fullWidth>
              <Typography className={classes.inputLabel} 
                title="Enter the stripe ID for coupon to be applied to these members.">
                  Coupon ID
                  <Icon className={classes.icon} iconName={SupportedIcons.InfoOutlined} color="primary" />
              </Typography>
              <TextField variant="outlined"
                type="text"
                name="input"
                fullWidth
                InputLabelProps={{ shrink: true }}
                onChange={(e) => { setCouponId(String(e.target.value)) }}
              />
            </FormControl>

        </Grid>
        {inProgress ? <p>Applying discounts. Please wait...this can take several minutes...</p> :
          <>
            <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-end", height: "2.85em", flexDirection: "column" }}>
              <Button
                variant="outlined"
                color="primary"
                disabled={Boolean(bulkListError)}
                onClick={() => { _submit() }}>
                Submit
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection: "column" }}>
              <BulkShowResultsListButton
                title={"Successfully Applied Discounts to " + (storedEmailList?.length || 0).toString() + " members"}
                emailList={storedEmailList}
                ignoreList={storedIgnoreList}
                hide={storedEmailList === undefined}
                csvName={"DiscountMembers" + new Date().toLocaleDateString()}
              />
            </Grid>
          </>
        }
        {(emailList.length > 0 || ignoreList.length > 0) && <BulkResponseDialog
          title="Successfully Applied Discounts"
          isOpen={isOpen}
          emailList={emailList}
              ignoreList={ignoreList}
              handleClose={() => {
                setIsOpen(false);
                setStoredEmailList(emailList);
                setStoredIgnoreList(ignoreList);
                setEmailList([]);
                setIgnoreList([]);
              }} />}
      </>
    )
}

export default DiscountMembers;
